// ── 전자메일 캐시(접속자 개인 PC 메모리 버전) ───────────────────────────────
// 2026-09 요청: "전자메일 로딩속도가 느린데 로그인하면 캐시로 내려받아두었다가 로그아웃되면
// 삭제하여 로딩속도를 높임" → 후속 피드백: "서버 메모리가 아니라 접속자 개인 PC의 메모리에
// 넣어야 할 것 같다(동시접속자가 많을 경우 서버과부하 방지)".
//
// 최초 구현은 backend/utils/emailCache.js — Node 서버 프로세스 메모리에 사용자별 캐시를 두는
// 방식이었다. 이 경우 동시접속자가 많아지면 그만큼 서버 프로세스(단일 Node 인스턴스) 메모리
// 사용량이 늘어나 서버 자체에 부담이 된다는 지적을 받고, 이 파일로 아예 캐시 위치를 서버가
// 아니라 "그 사람이 지금 쓰고 있는 브라우저 탭의 메모리"로 옮겼다. 브라우저 탭 메모리는 그
// 사람의 PC 자원이라 다른 사용자나 서버에는 전혀 영향이 없고, 동시접속자 수와 무관하게
// 서버 부하가 늘지 않는다.
//
// - 저장 위치: 이 모듈의 최상위 스코프에 있는 순수 JS Map. React 컴포넌트 상태가 아니라
//   모듈 스코프에 둔 이유는 Email.js 컴포넌트가 언마운트/재마운트(다른 메뉴 갔다가 돌아오는
//   경우 등)되어도 캐시가 살아있게 하기 위함이다. 반대로 브라우저 탭을 새로고침하거나 완전히
//   닫으면 이 Map도 그냥 사라진다(=서버 재시작 없이도 항상 최신 코드/빈 캐시로 시작).
// - "로그아웃되면 삭제": 서버 쪽 emailCache.clearUser()와 동일한 역할을 clearAll()이 담당하며,
//   frontend/src/context/AuthContext.js의 clearSessionLocally()(수동 로그아웃/자동 로그아웃/
//   다른 탭에서의 로그아웃 방송 수신을 포함해 "로그아웃이 로컬에 반영되는 모든 경로"가 결국
//   이 함수 하나로 모인다)에서 호출한다.
// - TTL은 서버 버전과 동일하게 유지: 폴더 목록 60초, 메일 목록 20초. 이유도 동일 — 메일은
//   실시간성이 있는 데이터라 너무 오래 캐시하면 새 메일이 늦게 보이므로, 짧은 시간만
//   재사용하고 그 사이 발송/삭제/읽음처리 등 변경이 생기면 각 지점에서 명시적으로
//   clearAccount()를 호출해 즉시 무효화한다(서버 버전의 emailCache.js와 동일한 설계).
// - 키 구조: `${accountId}::folders` 또는
//   `${accountId}::messages::${folder}::${page}::${pageSize}`
//   — 서버 버전과 달리 username을 넣지 않는다. 이 캐시 자체가 이미 그 사람의 브라우저(그
//   사람만 접근 가능한 메모리) 안에 있으므로 다른 사용자와 섞일 걱정이 없기 때문이다.

const store = new Map(); // key -> { data, expiresAt }

export const TTL = {
  folders: 60 * 1000,   // 폴더 목록: 1분
  messages: 20 * 1000,  // 메일 목록: 20초
};

export function keyFolders(accountId) {
  return `${accountId}::folders`;
}
export function keyMessages(accountId, folder, page, pageSize) {
  return `${accountId}::messages::${folder}::${page}::${pageSize}`;
}

export function get(key) {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    store.delete(key);
    return undefined;
  }
  return entry.data;
}

export function set(key, data, ttlMs) {
  store.set(key, { data, expiresAt: Date.now() + ttlMs });
}

// 발송/삭제/플래그변경/읽음처리 등으로 특정 계정의 데이터가 실제로 바뀐 직후 호출 — 다음
// 조회부터는 다시 서버에서 가져와서 캐시를 새로 채운다.
export function clearAccount(accountId) {
  const prefix = `${accountId}::`;
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
}

// 로그아웃 시점 — 이 브라우저 탭에 쌓인 모든 계정의 캐시를 통째로 비운다.
export function clearAll() {
  store.clear();
}
